import { useEffect, useRef, useState } from "react";

/* Stable default so the reference doesn't change every render */
const DEFAULT_PRELOAD = Object.freeze([
  "numpy",
  "pandas",
  "matplotlib",
  "scikit-learn",
]);

/**
 * React hook that loads Pyodide once and exposes run(code).
 * - Captures stdout/stderr
 * - Supports loading common packages
 * - Returns { ready, busy, output, run, clear, version, parseOutput }
 */
export default function usePyodideKernel(
  { preload = DEFAULT_PRELOAD } = {}
) {
  const [ready, setReady] = useState(false);
  const [busy, setBusy] = useState(true);
  const [output, setOutput] = useState("");
  const pyRef = useRef(null);

  useEffect(() => {
    let cancelled = false;

    (async () => {
      try {
        // Load pyodide runtime if missing
        if (!window.loadPyodide) {
          await new Promise((resolve, reject) => {
            const s = document.createElement("script");
            s.src = "https://cdn.jsdelivr.net/pyodide/v0.24.1/full/pyodide.js";
            s.onload = resolve;
            s.onerror = reject;
            document.head.appendChild(s);
          });
        }

        const py = await window.loadPyodide({
          stdout: (t) => setOutput((p) => p + t + "\n"),
          stderr: (t) => setOutput((p) => p + t + "\n"),
        });

        // preload packages available in Pyodide
        if (preload && preload.length > 0) {
          await py.loadPackage(preload);
        }

        // configure matplotlib emitter for PNG lines
        await py.runPythonAsync(`
import matplotlib, io, base64
matplotlib.use("agg")
def __stt_emit_png():
    import matplotlib.pyplot as plt
    import io, base64, sys
    buf = io.BytesIO()
    plt.savefig(buf, format="png", bbox_inches="tight")
    buf.seek(0)
    b64 = base64.b64encode(buf.read()).decode()
    print("__STT_IMAGE_PNG__" + b64)
`);

        if (cancelled) return;
        pyRef.current = py;
        setBusy(false);
        setReady(true);
      } catch (e) {
        if (!cancelled) setOutput(String(e));
        setBusy(false);
      }
    })();

    return () => {
      cancelled = true;
    };
    // ✅ dependency is a simple identifier; ESLint is happy
  }, [preload]);

  const parseOutput = (raw) => {
    const lines = raw.split("\n");
    const parts = [];
    for (const line of lines) {
      if (line.startsWith("__STT_IMAGE_PNG__")) {
        parts.push({ type: "img", b64: line.replace("__STT_IMAGE_PNG__", "") });
      } else if (line.trim() !== "") {
        parts.push({ type: "text", text: line });
      }
    }
    return parts;
  };

  const run = async (code) => {
    if (!pyRef.current) return;
    setBusy(true);
    setOutput("");
    try {
      await pyRef.current.runPythonAsync(code);
    } catch (e) {
      setOutput((p) => p + "\n" + String(e));
    } finally {
      setBusy(false);
    }
  };

  const clear = () => setOutput("");

  return {
    ready,
    busy,
    output,
    run,
    clear,
    version: "pyodide-0.24.1",
    parseOutput,
  };
}
